import React from "react";
import sessionApi from "@diditusa/shared/api/sessionApi";
import adminApi from "@diditusa/shared/api/adminApi";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import MetricsCard from "@diditusa/shared/web_components/MetricsCard/MetricsCard";
import { Box, Button, Typography, Grid, Container } from "@mui/material";

import { useNavigate } from "react-router-dom";
import userApi from "@diditusa/shared/api/userApi";
import { useAppSelector } from "../../hooks";
import BasicTable from "@diditusa/shared/web_components/BasicTable/BasicTable";
import { JobStatus } from "@diditusa/shared/types/jobTypes";
import { getErrorMessage } from "@diditusa/shared/api/apiUtils";
import {
  BasicTableHeader,
  WaitingListEntry,
} from "@diditusa/shared/types/commonTypes";
import { format } from "date-fns";
import { DateTime } from "luxon";

interface Metric {
  label: string;
  value: string;
}

export default function Home() {
  const {
    data: user,
    isLoading,
    isError,
    error,
  } = sessionApi.useLoadUserQuery();
  const { data: jobsSummary, ...getJobsSummaryState } =
    adminApi.useAdmingGetJobsSummaryQuery();
  const { data: waitingList, ...getWaitingListState } =
    adminApi.useGetWaitingListQuery();
  const navigate = useNavigate();
  const {
    data: appStats,
    isLoading: isAppStatsLoading,
    isError: isAppStatsError,
    error: appStatsError,
  } = adminApi.useAppStatsQuery();

  const sessionUser = useAppSelector((state) => state.root.sessionState.user);

  const [sendPhoneVerification, sendPhoneVerificationState] =
    userApi.useSendUserPhoneVerificationMutation();

  if (isLoading) {
    return <div>Loading user data...</div>;
  }

  if (isError) {
    return (
      <div>
        Error loading user: {(error as any)?.message || "Unknown error"}
      </div>
    );
  }

  // const statCard = (
  //   title: string,
  //   content: string | number | null,
  //   onClick: () => void
  // ) => (
  //   <Card
  //     onClick={onClick}
  //     sx={{
  //       width: 345,
  //       margin: 8,
  //       "&:hover": {
  //         boxShadow: 1,
  //         cursor: "pointer",
  //       },
  //     }}
  //     variant="outlined"
  //   >
  //     <CardHeader title={title} />
  //     <CardContent>
  //       <Typography variant="h1" component="div">
  //         {content}
  //       </Typography>
  //     </CardContent>
  //   </Card>
  // );

  // const renderAppStats = () => {
  //   let content;
  // if (isAppStatsLoading) {
  //   content = <LoadingIndicator isVisible={isAppStatsLoading} />;
  // }

  // if (isAppStatsError) {
  //   content = <p>Error loading app stats: {appStatsError.toString()}</p>;
  // }

  //   if (appStats) {
  //     content = (
  //       <Box display={"flex"} flexDirection={"row"} justifyContent={"center"}>
  //         {statCard("Total users", appStats.userCount, () =>
  //           navigate("/users")
  //         )}
  //         {statCard("Total companies", appStats.companyCount, () =>
  //           navigate("/companies")
  //         )}
  //         {statCard("Completed jobs", appStats.completedJobCount, () =>
  //           navigate("/jobs")
  //         )}
  //       </Box>
  //     );
  //   }
  //   return <div>{content}</div>;
  // };

  const onSendVerificationClick = async () => {
    if (!sessionUser) {
      return;
    }
    console.log(sessionUser.accessToken);
    await sendPhoneVerification({ accessToken: sessionUser!.accessToken! });

    if (sendPhoneVerificationState.isError) {
      console.log((sendPhoneVerificationState.error as any).data.message);
    }
  };

  const renderActiveJobsTable = () => {
    if (getJobsSummaryState.isLoading) {
      return <LoadingIndicator isVisible={getJobsSummaryState.isLoading} />;
    }

    if (getJobsSummaryState.isError) {
      return (
        <p>Error fetching jobs {getErrorMessage(getJobsSummaryState.error)}</p>
      );
    }

    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      phoneNumber: string;
      startDate: Date;
      startTime: Date;
      package: string;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      jobsSummary?.activeJobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        user: `${j.createdBy.firstName} ${j.createdBy.lastName}`,
        phoneNumber: j.createdBy ? `${j.createdBy?.phoneNumber}` : "-",
        startTime: j.startTime,
        startDate: j.startTime,
        package: j.securityOption?.title || "",
      })) || [];

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: "reservationCode", value: "Reservation Number" },
      { key: "user", value: "Customer" },
      { key: "phoneNumber", value: "Phone Number" },
      { key: "startDate", value: "Start Date" },
      { key: "startTime", value: "Start Time" },
      { key: "package", value: "Package" },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];

      if (field === "startDate") {
        return format(c.startDate, "MM/dd/yyyy");
      }

      if (field === "startTime") {
        return format(c.startTime, "hh:mm a");
      }
    };

    return (
      <Box sx={{ mb: 10 }}>
        <Typography variant="h6" gutterBottom>
          Live Jobs
        </Typography>
        <BasicTable
          headers={headers}
          data={jobDetails}
          onRowClick={(idx) => navigate(`/jobs/${jobDetails[idx].id}`)}
          getCustomComponentForField={getCustomComponentForCell}
        />
      </Box>
    );
  };

  const renderJobsTable = () => {
    if (getJobsSummaryState.isLoading) {
      return <LoadingIndicator isVisible={getJobsSummaryState.isLoading} />;
    }

    if (getJobsSummaryState.isError) {
      return (
        <p>Error fetching jobs {getErrorMessage(getJobsSummaryState.error)}</p>
      );
    }

    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      phoneNumber: string;
      location: string;
      startDate: DateTime;
      startTime: DateTime;
      package: string;
      status: JobStatus;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      jobsSummary?.newJobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        user: `${j.createdBy?.firstName} ${j.createdBy?.lastName}`,
        phoneNumber: j.createdBy ? `${j.createdBy?.phoneNumber}` : "-",
        startTime: j.startTime,
        status: j.status,
        location: j.location,
        startDate: j.startTime,
        package: j.securityOption?.title || "",
      })) || [];

    jobDetails.sort((jd1, jd2) => jd2.id - jd1.id);

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: "reservationCode", value: "Reservation Number" },
      { key: "user", value: "Customer" },
      { key: "phoneNumber", value: "Phone Number" },
      { key: "location", value: "Location" },
      { key: "startDate", value: "Start Date" },
      { key: "startTime", value: "Start Time" },
      { key: "package", value: "Package" },
      { key: "status", value: "Status" },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];

      if (field === "startDate") {
        return DateTime.fromISO(String(c.startDate)).toFormat("MM/dd/yyyy");
      }

      if (field === "startTime") {
        return DateTime.fromISO(String(c.startTime)).toFormat("hh:mm a ZZZZ");
      }
    };

    return (
      <Box sx={{ mb: 10 }}>
        <Typography variant="h6" gutterBottom>
          New Jobs
        </Typography>
        <BasicTable
          headers={headers}
          data={jobDetails}
          onRowClick={(idx) => navigate(`/jobs/${jobDetails[idx].id}`)}
          getCustomComponentForField={getCustomComponentForCell}
        />
      </Box>
    );
  };

  const renderWaitingListTable = () => {
    if (getWaitingListState.isLoading) {
      return <LoadingIndicator isVisible={getWaitingListState.isLoading} />;
    }

    if (getWaitingListState.isError) {
      return (
        <p>
          Error fetching waitingList{" "}
          {getErrorMessage(getWaitingListState.error)}
        </p>
      );
    }

    interface WaitingListWithId extends WaitingListEntry {
      id: string;
    }

    type WaitingListKey = keyof WaitingListWithId;
    const data = waitingList?.map((wl) => ({ id: wl.email, ...wl })) || [];

    const headers: BasicTableHeader<WaitingListKey>[] = [
      { key: "email", value: "Email" },
      { key: "createdAt", value: "Added" },
    ];

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Waiting List
        </Typography>
        <BasicTable headers={headers} data={data || []} />
      </Box>
    );
  };
  const renderMetrics = () => {
    const metrics: Metric[] = [
      {
        label: "Total Jobs",
        value: `${appStats ? appStats.completedJobCount : "-"}`,
      },
      {
        label: "Active Users",
        value: `${appStats ? appStats.userCount : "-"}`,
      },
      { label: "Pending Payments", value: "$12,450" },
      { label: "Customer Satisfaction", value: "4.8 / 5" },
    ];

    if (isAppStatsLoading) {
      return <LoadingIndicator isVisible={isAppStatsLoading} />;
    }

    if (isAppStatsError) {
      return <p>Error loading app stats: {appStatsError.toString()}</p>;
    }
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics.map((metric) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={metric.label}>
            <MetricsCard metric={metric} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container>
      <Typography variant="h3">SAëF Admin Dashboard</Typography>
      {user && (
        <Box display={"flex"} flexDirection={"column"}>
          <p>
            Welcome, {user.firstName} {user.lastName}!
          </p>
          {/* Display other user information as needed */}
          {renderActiveJobsTable()}
          {renderJobsTable()}

          {renderWaitingListTable()}
          {renderMetrics()}
        </Box>
      )}
    </Container>
  );
}
